import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Users, Check, Clock, X, AlertCircle, Loader2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface TeamApprovalCardProps {
  verification: any;
  onUpdate: () => void;
}

const TeamApprovalCard = ({ verification, onUpdate }: TeamApprovalCardProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const isEmailVerified = verification?.email_verified === true;
  const isSelfieVerified = verification?.selfie_verified === true;
  const canRequestApproval = isEmailVerified && isSelfieVerified;
  
  const status = verification?.status;
  const rejectionReason = verification?.rejection_reason;

  const submitForApproval = async () => {
    if (!canRequestApproval) {
      toast.error('Complete a verificação de email e selfie primeiro');
      return;
    }

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('document_verifications')
        .update({ 
          status: 'waiting',
          submitted_at: new Date().toISOString()
        })
        .eq('id', verification.id);

      if (error) throw error;

      toast.success('Perfil enviado para análise!');
      onUpdate();
    } catch (error: any) {
      toast.error('Erro ao submeter para aprovação');
    } finally {
      setIsLoading(false);
    }
  };

  const getCardStatus = () => {
    if (status === 'approved') return 'approved';
    if (status === 'rejected') return 'rejected';
    if (status === 'waiting') return 'waiting';
    if (canRequestApproval) return 'ready';
    return 'blocked';
  };

  const cardStatus = getCardStatus();

  const getStatusColor = () => {
    switch (cardStatus) {
      case 'approved': return 'border-green-500/50 bg-green-500/10';
      case 'rejected': return 'border-red-500/50 bg-red-500/10';
      case 'waiting': return 'border-blue-500/50 bg-blue-500/10';
      case 'ready': return 'border-yellow-500/50 bg-yellow-500/10';
      default: return 'border-white/10 bg-white/5';
    }
  };

  const getStatusIcon = () => {
    switch (cardStatus) {
      case 'approved': return <Check className="w-4 h-4" />;
      case 'rejected': return <X className="w-4 h-4" />;
      case 'waiting': return <Clock className="w-4 h-4" />;
      case 'ready': return <AlertCircle className="w-4 h-4" />;
      default: return <Users className="w-4 h-4" />;
    }
  };

  const getStatusBgColor = () => {
    switch (cardStatus) {
      case 'approved': return 'bg-green-500 text-white';
      case 'rejected': return 'bg-red-500 text-white';
      case 'waiting': return 'bg-blue-500 text-white';
      case 'ready': return 'bg-yellow-500 text-white';
      default: return 'bg-white/20 text-white/80';
    }
  };

  return (
    <Card className={`transition-all duration-300 ${getStatusColor()}`}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg text-white">
          <div className={`p-2 rounded-full ${getStatusBgColor()}`}>
            {getStatusIcon()}
          </div>
          Aprovação do Time
        </CardTitle>
      </CardHeader>
      
      <CardContent className="space-y-4">
        {!canRequestApproval && (
          <div className="text-center py-6">
            <div className="w-12 h-12 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-3">
              <Users className="w-6 h-6 text-white/40" />
            </div>
            <p className="text-white/60 text-sm font-medium mb-1">
              Etapa bloqueada
            </p>
            <p className="text-white/40 text-xs mb-3">
              Complete as verificações anteriores primeiro:
            </p>
            <div className="space-y-2 text-xs">
              <div className={`flex items-center justify-center gap-2 ${isEmailVerified ? 'text-green-400' : 'text-white/40'}`}>
                {isEmailVerified ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                <span className="font-medium">Email verificado</span>
              </div>
              <div className={`flex items-center justify-center gap-2 ${isSelfieVerified ? 'text-green-400' : 'text-white/40'}`}>
                {isSelfieVerified ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
                <span className="font-medium">Selfie verificada</span>
              </div>
            </div>
          </div>
        )}

        {canRequestApproval && status === 'pending' && (
          <div className="text-center space-y-3">
            <p className="text-sm text-white/60">
              Pronto para análise do time Interlaced
            </p>
            <Button 
              onClick={submitForApproval}
              disabled={isLoading}
              className="w-full bg-luxury-red hover:bg-luxury-red/90"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                'Submeter para Aprovação'
              )}
            </Button>
          </div>
        )}

        {status === 'waiting' && (
          <div className="text-center py-4">
            <div className="text-blue-400 font-medium flex items-center justify-center gap-2">
              <Clock className="w-5 h-5" />
              Perfil em análise
            </div>
            {verification?.submitted_at && (
              <p className="text-sm text-white/60 mt-1">
                Submetido em {new Date(verification.submitted_at).toLocaleDateString('pt-BR')}
              </p>
            )}
            <p className="text-sm text-white/60 mt-1">
              Seu perfil está em análise pela equipe. Em breve você receberá uma resposta sobre a verificação.
            </p>
          </div>
        )}

        {status === 'approved' && (
          <div className="text-center py-4">
            <div className="text-green-400 font-medium flex items-center justify-center gap-2">
              <Check className="w-5 h-5" />
              Perfil aprovado!
            </div>
            <p className="text-sm text-white/60 mt-1">
              Aprovado em {new Date(verification.reviewed_at).toLocaleDateString('pt-BR')}
            </p>
          </div>
        )}

        {status === 'rejected' && (
          <div className="text-center space-y-3">
            <div className="text-red-400 font-medium flex items-center justify-center gap-2">
              <X className="w-5 h-5" />
              Perfil rejeitado
            </div>
            {rejectionReason && (
              <div className="p-3 bg-red-500/10 rounded-lg border border-red-500/30">
                <p className="text-sm text-red-300">
                  <strong>Motivo:</strong> {rejectionReason}
                </p>
              </div>
            )}
            <Button 
              onClick={submitForApproval}
              disabled={isLoading}
              variant="outline"
              className="w-full border-white/20 text-gray-900 hover:bg-white/10 bg-white"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Reenviando...
                </>
              ) : (
                'Submeter Novamente'
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TeamApprovalCard;